import { useForm } from '@tanstack/react-form';
import React, { FC, useCallback, useMemo, useState } from 'react';

import Plus from '@gravity-ui/icons/Plus';
import Xmark from '@gravity-ui/icons/Xmark';
import { Button, Checkbox, Icon, TextInput, useToaster } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { ApiError } from 'client/api/error';
import {
    getAliceAgentAppUserAgreements,
    publishAliceAgentApp,
    updateAliceAgentApp,
} from 'client/api/services/aliceAgentApps';
import { AliceAgentAppCoverUploader } from 'client/blocks/AliceAgentAppCoverUploader/AliceAgentAppCoverUploader';
import { Field } from 'client/blocks/form/Field/Field';
import { UserAgreements } from 'client/blocks/UserAgreements/UserAgreements';
import { wrapInText } from 'client/lib/i18n-utils';
import { routes } from 'client/lib/routes';
import { AliceAgentAppDTO, AliceAgentAppManifest, AliceInternalAgentAppConfig } from 'client/model/aliceAgentApp';
import {
    reconcileAgreementIds,
    sortUserAgreements,
    syncUserAgreementsOnChange,
    UserAgreement,
    UserAgreementOrDraft,
} from 'client/model/userAgreement';
import { canUseAliceAgentAppCategory, canUseAliceAgentAppCover } from 'client/utils';
import { translateAppStoreError } from 'client/utils/errorMessage';
import * as formKeyset from 'i18n/AliceAgentAppForm';

import { AliceAgentAppCategoryField } from './AliceAgentAppCategoryField';
import styles from './AliceAgentAppEditPage.module.scss';
import { AliceAgentAppLogoUploader } from './AliceAgentAppLogoUploader';
import { AliceAgentAppPublishedActions } from './AliceAgentAppPublishedActions';
import { fromManifestMap, LocalizedTextInputs, toManifestMap } from './LocalizedTextInputs';
import { agentAppServerKeyMap, FormState, validators } from './validation';

const tForm = i18n(formKeyset);

interface Props {
    app: AliceAgentAppDTO;
    isDraft: boolean;
    fetchedUserAgreements: UserAgreement[];
    onSaved: () => Promise<void> | void;
}

const readSource = (app: AliceAgentAppDTO, isDraft: boolean) =>
    isDraft
        ? { name: app.draft.name, logo: app.draft.logo, cover: app.draft.cover, manifest: app.draft.manifest }
        : {
              name: app.published?.name ?? '',
              logo: app.published?.logo,
              cover: app.published?.cover,
              manifest: app.published?.manifest,
          };

const toFormState = (app: AliceAgentAppDTO, isDraft: boolean, userAgreements: UserAgreementOrDraft[]): FormState => {
    const src = readSource(app, isDraft);
    const config = src.manifest?.config as AliceInternalAgentAppConfig | undefined;
    return {
        name: src.name,
        agentName: config?.agentName ?? '',
        title: fromManifestMap(src.manifest?.title),
        shortDescription: fromManifestMap(src.manifest?.shortDescription),
        aliases: (src.manifest?.aliases ?? []).length > 0 ? (src.manifest?.aliases as string[]) : [''],
        availableInPlusMenu: src.manifest?.availableInPlusMenu ?? false,
        availableWithoutLogin: src.manifest?.availableWithoutLogin ?? false,
        logoId: src.logo?.id,
        logoUrl: src.logo?.url,
        coverId: src.cover?.id,
        coverUrl: src.cover?.url,
        chatTestingEnabled: isDraft ? app.draft.chatTestingEnabled : false,
        userAgreements,
        categoryName: (isDraft ? app.draft.categoryName : app.published?.categoryName) ?? '',
    };
};

const buildManifest = (form: FormState): AliceAgentAppManifest => ({
    type: 'aliceInternalAgentApp',
    config: { agentName: form.agentName } satisfies AliceInternalAgentAppConfig,
    title: toManifestMap(form.title),
    shortDescription: toManifestMap(form.shortDescription),
    aliases: form.aliases.map((s) => s.trim()).filter(Boolean),
    availableInPlusMenu: form.availableInPlusMenu,
    availableWithoutLogin: form.availableWithoutLogin,
});

export const AliceAgentAppEditPage: FC<Props> = ({ app, isDraft, fetchedUserAgreements, onSaved }) => {
    const { add: toasterAdd } = useToaster();
    const canUseCategory = canUseAliceAgentAppCategory();
    const canUseCover = canUseAliceAgentAppCover();

    const initial = useMemo(
        () => toFormState(app, isDraft, fetchedUserAgreements),
        [app, isDraft, fetchedUserAgreements],
    );
    const [isPublishing, setIsPublishing] = useState(false);
    const readOnly = !isDraft;

    const form = useForm({
        defaultValues: initial,
        onSubmit: async ({ value, formApi }) => {
            // Separate try/catch: agreement errors are keyed `name`/`url` and would collide
            // with the agent fields in setErrorMap, so they go to the toast only.
            try {
                await updateAliceAgentApp(app.skillId, {
                    name: value.name,
                    manifest: buildManifest(value),
                    chatTestingEnabled: value.chatTestingEnabled,
                    ...(value.logoId !== initial.logoId && value.logoId ? { logoId: value.logoId } : {}),
                    ...(canUseCover && value.coverId !== initial.coverId ? { coverId: value.coverId ?? null } : {}),
                    ...(canUseCategory && value.categoryName ? { categoryName: value.categoryName } : {}),
                });
            } catch (err) {
                let toastMessage = tForm('toast-save-error');
                if (err instanceof ApiError && err.payload?.typedErrors?.length) {
                    const fieldErrors: Partial<Record<keyof FormState, string>> = {};
                    for (const apiErr of err.payload.typedErrors) {
                        if (!apiErr.key) continue;
                        const fieldKey = agentAppServerKeyMap[apiErr.key];
                        if (!fieldKey) continue;
                        fieldErrors[fieldKey] = translateAppStoreError(apiErr.type, apiErr.arg);
                    }
                    formApi.setErrorMap({
                        onSubmit: { form: '', fields: fieldErrors },
                    });
                    const first = err.payload.typedErrors[0];
                    toastMessage = translateAppStoreError(first.type, first.arg);
                    // Scroll to the first invalid field after React paints the aria-invalid attribute.
                    requestAnimationFrame(() => {
                        const invalid = document.querySelector('[aria-invalid="true"]');
                        if (invalid) {
                            invalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            if (invalid instanceof HTMLElement) invalid.focus({ preventScroll: true });
                        }
                    });
                } else if (err && typeof err === 'object' && 'message' in err && typeof err.message === 'string') {
                    toastMessage = err.message;
                }
                toasterAdd({ title: toastMessage, theme: 'danger', name: 'SAVE_ALICE_AGENT_APP' });
                return;
            }

            try {
                await syncUserAgreementsOnChange(app.skillId, initial.userAgreements, value.userAgreements);
                await onSaved();
                // Reset with the server snapshot so new rows carry ids — a no-id draft would
                // otherwise re-`create` on the next save and hit the backend's unique name/url 400.
                const fresh = await getAliceAgentAppUserAgreements(app.skillId, isDraft);
                formApi.reset({ ...value, userAgreements: sortUserAgreements(fresh) });
                toasterAdd({ title: tForm('toast-saved'), theme: 'success', name: 'SAVE_ALICE_AGENT_APP' });
            } catch (err) {
                const message =
                    err instanceof ApiError && err.payload?.typedErrors?.length
                        ? translateAppStoreError(err.payload.typedErrors[0].type, err.payload.typedErrors[0].arg)
                        : err instanceof Error && err.message
                          ? err.message
                          : tForm('toast-save-error');
                // Manifest is already saved. Keep the user's agreement input on screen, but stamp
                // server ids onto rows that already got created in the partially-applied batch —
                // otherwise a retry would re-`create` them and hit the backend's unique name/url
                // 400 on a row the user never touched.
                let preserved: UserAgreementOrDraft[] = value.userAgreements;
                try {
                    const fresh = sortUserAgreements(await getAliceAgentAppUserAgreements(app.skillId, isDraft));
                    preserved = reconcileAgreementIds(value.userAgreements, fresh);
                } catch {
                    // refetch failed — keep raw input so the user doesn't lose their typing
                }
                formApi.reset({ ...value, userAgreements: preserved });
                toasterAdd({ title: message, theme: 'danger', name: 'SAVE_ALICE_AGENT_APP' });
            }
        },
    });

    const handleLogoUploaded = useCallback(
        (image: { id: string; url: string }) => {
            form.setFieldValue('logoId', image.id);
            form.setFieldValue('logoUrl', image.url);
        },
        [form],
    );

    const handleLogoError = (message: string) => {
        toasterAdd({ title: message, theme: 'danger', name: 'UPLOAD_ALICE_AGENT_APP_LOGO' });
    };

    const handleCoverUploaded = useCallback(
        (image: { id: string; url: string }) => {
            form.setFieldValue('coverId', image.id);
            form.setFieldValue('coverUrl', image.url);
        },
        [form],
    );

    const handleCoverDeleted = useCallback(() => {
        form.setFieldValue('coverId', undefined);
        form.setFieldValue('coverUrl', undefined);
    }, [form]);

    const handleCoverError = (message: string) => {
        toasterAdd({ title: message, theme: 'danger', name: 'UPLOAD_ALICE_AGENT_APP_COVER' });
    };

    const handlePublish = async () => {
        if (!isDraft || isPublishing || form.state.isDirty) return;
        setIsPublishing(true);
        try {
            await publishAliceAgentApp(app.skillId);
            await onSaved();
            toasterAdd({ title: tForm('toast-published'), theme: 'success', name: 'PUBLISH_ALICE_AGENT_APP' });
        } catch (err) {
            const message =
                err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
                    ? err.message
                    : tForm('toast-publish-error');
            toasterAdd({ title: message, theme: 'danger', name: 'PUBLISH_ALICE_AGENT_APP' });
        } finally {
            setIsPublishing(false);
        }
    };

    return (
        <>
            <form
                className={styles.formContainer}
                id="main"
                onSubmit={(e) => {
                    e.preventDefault();
                    form.handleSubmit();
                }}
            >
                <form.Field name="name" validators={{ onChange: validators.name, onSubmit: validators.name }}>
                    {(field) => (
                        <Field required fieldTitle={tForm('field-name')} error={field.state.meta.errors[0]}>
                            <TextInput
                                className={styles.formElement}
                                value={field.state.value}
                                onUpdate={field.handleChange}
                                onBlur={field.handleBlur}
                                disabled={readOnly}
                                size="xl"
                                placeholder={tForm('field-name-placeholder')}
                                validationState={field.state.meta.errors[0] ? 'invalid' : undefined}
                            />
                        </Field>
                    )}
                </form.Field>

                <form.Field name="logoId" validators={{ onChange: validators.logoId, onSubmit: validators.logoId }}>
                    {(field) => (
                        <Field required fieldTitle={tForm('field-logo')} error={field.state.meta.errors[0]}>
                            <form.Subscribe selector={(s) => s.values.logoUrl}>
                                {(logoUrl) => (
                                    <AliceAgentAppLogoUploader
                                        skillId={app.skillId}
                                        logoUrl={logoUrl}
                                        disabled={readOnly}
                                        onUploaded={handleLogoUploaded}
                                        onError={handleLogoError}
                                    />
                                )}
                            </form.Subscribe>
                        </Field>
                    )}
                </form.Field>

                {canUseCover && (
                    <form.Subscribe selector={(s) => s.values.coverUrl}>
                        {(coverUrl) => (
                            <AliceAgentAppCoverUploader
                                skillId={app.skillId}
                                coverUrl={coverUrl}
                                disabled={readOnly}
                                onUploaded={handleCoverUploaded}
                                onDeleted={handleCoverDeleted}
                                onError={handleCoverError}
                            />
                        )}
                    </form.Subscribe>
                )}

                <form.Field
                    name="agentName"
                    validators={{ onChange: validators.agentName, onSubmit: validators.agentName }}
                >
                    {(field) => (
                        <Field
                            required
                            fieldTitle={tForm('field-agent-name')}
                            fieldDescription={tForm('field-agent-name-description')}
                            error={field.state.meta.errors[0]}
                        >
                            <TextInput
                                className={styles.formElement}
                                value={field.state.value}
                                onUpdate={field.handleChange}
                                onBlur={field.handleBlur}
                                disabled={readOnly}
                                size="xl"
                                placeholder={tForm('field-agent-name-placeholder')}
                                validationState={field.state.meta.errors[0] ? 'invalid' : undefined}
                            />
                        </Field>
                    )}
                </form.Field>

                <form.Field name="title">
                    {(field) => (
                        <Field fieldTitle={tForm('field-title')} fieldDescription={tForm('field-title-description')}>
                            <LocalizedTextInputs
                                value={field.state.value}
                                onUpdate={field.handleChange}
                                disabled={readOnly}
                            />
                        </Field>
                    )}
                </form.Field>

                <form.Field name="shortDescription">
                    {(field) => (
                        <Field
                            fieldTitle={tForm('field-short-description')}
                            fieldDescription={tForm('field-short-description-description')}
                        >
                            <LocalizedTextInputs
                                value={field.state.value}
                                onUpdate={field.handleChange}
                                disabled={readOnly}
                            />
                        </Field>
                    )}
                </form.Field>

                {canUseCategory && (
                    <form.Field name="categoryName">
                        {(field) => (
                            <AliceAgentAppCategoryField
                                value={field.state.value}
                                onUpdate={field.handleChange}
                                disabled={readOnly}
                                error={field.state.meta.errors[0]}
                                fieldTitle={tForm('field-category')}
                                placeholder={tForm('field-category-placeholder')}
                                className={styles.formElement}
                            />
                        )}
                    </form.Field>
                )}

                <form.Field name="aliases" mode="array">
                    {(field) => (
                        <Field
                            fieldTitle={tForm('field-aliases')}
                            fieldDescription={wrapInText(
                                (s) => <kbd className={styles.kbd}>{s}</kbd>,
                                (s) => <kbd className={styles.kbd}>{s}</kbd>,
                            )(tForm('field-aliases-description'))}
                        >
                            {field.state.value.map((_, i) => (
                                <form.Field key={i} name={`aliases[${i}]`}>
                                    {(subField) => (
                                        <TextInput
                                            className={styles.formElement}
                                            value={subField.state.value as string}
                                            onUpdate={subField.handleChange}
                                            endContent={
                                                field.state.value.length > 1 ? (
                                                    <Button
                                                        view="flat"
                                                        disabled={readOnly}
                                                        onClick={() => field.removeValue(i)}
                                                    >
                                                        <Icon data={Xmark} />
                                                    </Button>
                                                ) : undefined
                                            }
                                            size="xl"
                                            disabled={readOnly}
                                            placeholder={tForm('field-aliases-placeholder')}
                                        />
                                    )}
                                </form.Field>
                            ))}
                            <Button
                                className={styles.formButton}
                                disabled={readOnly}
                                onClick={() => field.pushValue('')}
                            >
                                <Icon data={Plus} />
                                {tForm('field-aliases-add')}
                            </Button>
                        </Field>
                    )}
                </form.Field>

                <section id="user-agreements">
                    <form.Field name="userAgreements">
                        {(field) => (
                            <UserAgreements
                                agreements={field.state.value}
                                onChange={field.handleChange}
                                disabled={form.state.isSubmitting}
                                isEditable={isDraft}
                            />
                        )}
                    </form.Field>
                </section>

                <form.Field name="availableInPlusMenu">
                    {(field) => (
                        <Field fieldTitle={tForm('field-placement')}>
                            <Checkbox
                                className={styles.formElement}
                                checked={field.state.value}
                                onChange={(e) => field.handleChange(e.target.checked)}
                                disabled={readOnly}
                                size="l"
                            >
                                {tForm('field-available-in-plus-menu')}
                            </Checkbox>
                        </Field>
                    )}
                </form.Field>

                <form.Field name="availableWithoutLogin">
                    {(field) => (
                        <Field fieldTitle={tForm('field-access')}>
                            <Checkbox
                                className={styles.formElement}
                                checked={field.state.value}
                                onChange={(e) => field.handleChange(e.target.checked)}
                                disabled={readOnly}
                                size="l"
                            >
                                {tForm('field-available-without-login')}
                            </Checkbox>
                        </Field>
                    )}
                </form.Field>

                <form.Field name="chatTestingEnabled">
                    {(field) => (
                        <Field fieldTitle={tForm('field-chat-testing')}>
                            <Checkbox
                                className={styles.formElement}
                                checked={field.state.value}
                                onChange={(e) => field.handleChange(e.target.checked)}
                                disabled={readOnly}
                                size="l"
                            >
                                {tForm('field-chat-testing-label')}
                            </Checkbox>
                        </Field>
                    )}
                </form.Field>
            </form>

            {isDraft && (
                <form.Subscribe selector={(s) => [s.isDirty, s.isSubmitting] as const}>
                    {([isDirty, isSubmitting]) => (
                        <div className={styles.footer}>
                            <Button
                                view="action"
                                size="l"
                                onClick={() => form.handleSubmit()}
                                disabled={!isDirty || isSubmitting || isPublishing}
                                loading={isSubmitting}
                            >
                                {tForm('action-save')}
                            </Button>
                            <Button
                                view="outlined-action"
                                size="l"
                                onClick={handlePublish}
                                disabled={isDirty || isSubmitting || isPublishing}
                                loading={isPublishing}
                            >
                                {tForm('action-publish')}
                            </Button>
                        </div>
                    )}
                </form.Subscribe>
            )}
            {!isDraft && (
                <AliceAgentAppPublishedActions
                    app={app}
                    draftUrl={routes.app.aliceAgentApp(app.skillId, true)}
                    onSaved={onSaved}
                />
            )}
        </>
    );
};
